import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert } from 'reactstrap'
import {
  approveVehicleCerts,
  service29Login,
  VehicleCert,
  VehicleCertQuery
} from '../../services/endpoints/service29'
import useCertList from './useCertList'
import DataGrid, {
  Column,
  LoadPanel,
  Paging,
  Sorting
} from 'devextreme-react/data-grid'
import {
  certSortOrderFor,
  handleCertSortChange,
  makeDateRenderer,
  makeStatusRenderer,
  renderUser
} from './Service29Utils'
import { useTimezone } from '../../hooks/useTimezone'
import CertPager from './CertPager'
import PageSizeSelect from './PageSizeSelect'
import CertSelectAll from './CertSelectAll'
import { Service29CancelButton } from './Service29CancelButton'
import { service29RequiresAuth } from '../../redux/slices'
import { useSelector } from 'react-redux'
import CertFilters, {
  CertDateRange,
  CertFilterField,
  CertFilterValues,
  CertStatusOption,
  dateRangeParams
} from './CertFilters'

const PENDING_STATUSES = ['requested', 'needs_approval']

const BASE_QUERY: VehicleCertQuery = {
  status: PENDING_STATUSES,
  sort: 'status',
  order: 'asc',
  max: 50,
  offset: 0
}

const FILTER_FIELDS: CertFilterField[] = [
  'vehicleVin',
  'deviceSerial',
  'requestedByUsername'
]

const STATUS_OPTIONS: CertStatusOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'requested', label: 'Requested' },
  { value: 'needs_approval', label: 'Needs Approval' }
]

const DATE_RANGES: CertDateRange[] = [
  { field: 'requestedAt', label: 'Requested At' }
]

const renderStatus = makeStatusRenderer({
  requested: { label: 'Requested', color: 'info' },
  needs_approval: { label: 'Needs Approval', color: 'warning' }
})

const Service29PendingTab = () => {
  const { formatLocalTimeFromUtc } = useTimezone()
  const renderDate = useMemo(() => makeDateRenderer(formatLocalTimeFromUtc), [formatLocalTimeFromUtc])
  const [query, setQuery] = useState<VehicleCertQuery>(BASE_QUERY)
  const { certs, total, error, refresh } = useCertList(true, query)
  const [alert, setAlert] = useState<{ message: string; color: string } | null>(
    null
  )
  const [selectedCerts, setSelectedCerts] = useState<VehicleCert[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const requiresAuth = useSelector(service29RequiresAuth)

  useEffect(() => {
    setSelectedCerts([])
  }, [query])

  const handleSearch = (filters: CertFilterValues) => {
    setQuery((prev) => ({
      ...prev,
      vehicleVin: filters.vehicleVin.trim() || undefined,
      deviceSerial: filters.deviceSerial.trim() || undefined,
      requestedByUsername: filters.requestedByUsername.trim() || undefined,
      ...dateRangeParams(filters.dates),
      status: filters.status ? [filters.status] : PENDING_STATUSES,
      offset: 0
    }))
  }

  const toggleCert = (cert: VehicleCert) => {
    setSelectedCerts((prev) =>
      prev.some((c) => c.id === cert.id)
        ? prev.filter((c) => c.id !== cert.id)
        : [...prev, cert]
    )
  }

  const handleBulkApprove = async () => {
    if (!window.confirm(`Approve ${selectedCerts.length} cert request(s)?`)) {
      return
    }

    const ids = selectedCerts.map((c) => c.id)

    setBulkLoading(true)

    try {
      await approveVehicleCerts(ids)
      setAlert({
        message: `Successfully approved ${ids.length} cert(s)`,
        color: 'success'
      })
    } catch (error) {
      setAlert({
        message: `${error.message}: An error occurred when trying to approve vehicle certs`,
        color: 'danger'
      })
    } finally {
      await refresh()
      setSelectedCerts([])
      setBulkLoading(false)
    }
  }

  const loginIntervalRef = useRef<NodeJS.Timeout | undefined>()

  useEffect(() => {
    return () => {
      if (loginIntervalRef.current) clearInterval(loginIntervalRef.current)
    }
  }, [])

  const handleLogin = async () => {
    if (!requiresAuth) return
    const ids = selectedCerts.map((c) => c.id)

    if (!window.confirm(`Approve ${selectedCerts.length} cert request(s)?`)) {
      return
    }

    if (loginIntervalRef.current) clearInterval(loginIntervalRef.current)

    setBulkLoading(true)

    try {
      const { url } = await service29Login(ids)
      const popup = window.open(url, 'Login', 'width=600,height=600')

      if (!popup) {
        setAlert({
          message: 'Login popup blocked - please allow popups for this site',
          color: 'warning'
        })
        setBulkLoading(false)
        return
      }

      loginIntervalRef.current = setInterval(() => {
        if (popup?.closed && loginIntervalRef.current) {
          clearInterval(loginIntervalRef.current)
          setBulkLoading(false)
        }
      }, 200)
    } catch (error) {
      setAlert({
        message: 'An error occurred when trying to login',
        color: 'danger'
      })
      setBulkLoading(false)
    }
  }

  const handleLoginSuccess = useCallback(
    async (event: MessageEvent) => {
      type LoginMessage = {
        type: 'auth-success' | 'auth-error'
        error?: string
      }
      const message = event.data?.type ? (event.data as LoginMessage) : null
      if (!message) return
      switch (message.type) {
        case 'auth-success':
          setAlert({
            message: 'Successfully approved vehicle certs',
            color: 'success'
          })
          await refresh()
          setSelectedCerts([])
          break
        case 'auth-error':
          setAlert({
            message: 'An error occurred when trying to approve vehicle certs',
            color: 'danger'
          })
          break
      }
      setBulkLoading(false)
    },
    [refresh]
  )

  useEffect(() => {
    window.addEventListener('message', handleLoginSuccess)
    return () => window.removeEventListener('message', handleLoginSuccess)
  }, [handleLoginSuccess])

  const handleApprove = requiresAuth ? handleLogin : handleBulkApprove

  return (
    <section className="cert-tab-section">
      {(alert || error) && (
        <Alert color={alert?.color ?? 'danger'} toggle={() => setAlert(null)}>
          {alert?.message ?? error}
        </Alert>
      )}
      <CertFilters
        fields={FILTER_FIELDS}
        statusOptions={STATUS_OPTIONS}
        dateRanges={DATE_RANGES}
        onSearch={handleSearch}
      />
      <menu className="cert-tab-toolbar">
        <li>
          {selectedCerts.length > 0 && (
            <button
              className="cert-bulk-action approve"
              disabled={bulkLoading}
              onClick={handleApprove}
            >
              {bulkLoading
                ? 'Approving…'
                : `Approve Selected (${selectedCerts.length})`}
            </button>
          )}
        </li>
        <li>
          <PageSizeSelect
            value={query.max ?? 50}
            onChange={(max) =>
              setQuery((prev) => ({ ...prev, max, offset: 0 }))
            }
          />
        </li>
      </menu>
      <DataGrid
        dataSource={certs}
        keyExpr="id"
        showBorders={true}
        rowAlternationEnabled={true}
        columnAutoWidth={true}
        allowColumnResizing={true}
        onOptionChanged={(e) => handleCertSortChange(e, setQuery, BASE_QUERY)}
      >
        <LoadPanel enabled />
        <Paging enabled={false} />
        <Sorting mode="single" />
        <Column
          width={70}
          allowSorting={false}
          allowResizing={false}
          caption="Cancel"
          cellRender={({ data }: { data: VehicleCert }) => (
            <Service29CancelButton
              cert={data}
              onSuccess={async () => {
                await refresh()
                setSelectedCerts((prev) => prev.filter((c) => c.id !== data.id))
                setAlert({
                  message: 'Successfully cancelled pending vehicle cert',
                  color: 'success'
                })
              }}
              onError={() =>
                setAlert({
                  message: 'Failed to cancel pending vehicle cert',
                  color: 'danger'
                })
              }
            />
          )}
        />
        <Column
          dataField="vehicle.displayName"
          caption="Vehicle"
          allowSorting={false}
        />
        <Column dataField="vehicle.vin" caption="VIN" allowSorting={false} />
        <Column
          dataField="device.serial"
          caption="Device Serial"
          allowSorting={false}
        />
        <Column
          dataField="device.type"
          caption="Device Type"
          allowSorting={false}
        />
        <Column
          dataField="status"
          caption="Status"
          cellRender={renderStatus}
          sortOrder={certSortOrderFor(query, 'status')}
        />
        <Column
          dataField="requestedBy"
          caption="Requested By"
          cellRender={renderUser}
          allowSorting={false}
        />
        <Column
          dataField="requestedAt"
          caption="Requested At"
          cellRender={renderDate}
          sortOrder={certSortOrderFor(query, 'requestedAt')}
        />
        <Column
          width={100}
          allowSorting={false}
          headerCellRender={() => (
            <CertSelectAll
              certs={certs}
              eligibleStatus="needs_approval"
              variant="approve"
              label="Select All"
              selectedCerts={selectedCerts}
              onChange={setSelectedCerts}
            />
          )}
          cellRender={({ data }: { data: VehicleCert }) => {
            if (data.status !== 'needs_approval') return null
            const selected = selectedCerts.some((c) => c.id === data.id)
            return (
              <>
                <input
                  type="checkbox"
                  id={`cert-select-${data.id}`}
                  className="cert-select-check approve"
                  checked={selected}
                  onChange={() => toggleCert(data)}
                />
                <label htmlFor={`cert-select-${data.id}`}>Approve</label>
              </>
            )
          }}
        />
      </DataGrid>
      <CertPager
        total={total}
        offset={query.offset ?? 0}
        max={query.max ?? 50}
        onPageChange={(offset) => setQuery((prev) => ({ ...prev, offset }))}
      />
    </section>
  )
}

export default Service29PendingTab
